/**
 * Administrators: OgreClan AI's scores for every coach's roster in the VGC divisions, against the Power Rankings rubric
 * (apps/api/src/ai/roster-scores.ts), with each category's reason and a paragraph of commentary. Editorial preseason
 * scores, not results. A run scores every roster in the background; this page follows it.
 */
import { useEffect, useState } from 'preact/hooks';
import { api } from '../api';
import { useMe } from '../me';
import { Link } from '../router';
import { ActionButton, ago, ErrorBox, Loading, toast } from '../ui';

interface Category { id: string, name: string, max: number, evaluate: string }
interface Score {
	leagueId: number;
	playerId: number;
	coach: string;
	pool: string;
	roster: { pokemon: string, cost: number, teraCaptain: boolean }[];
	scores: Record<string, number>;
	notes: Record<string, string>;
	strengths: string[];
	weaknesses: string[];
	comments: string;
	total: number;
	band: string;
	model: string;
	scoredAt: string;
}
interface View {
	enabled: boolean;
	rubric: Category[];
	outOf: number;
	status: {
		running: boolean, startedAt: string, finishedAt: string | null, by: string, total: number, done: number,
		failed: { league: string, coach: string, error: string }[], stopped: string | null,
	} | null;
	leagues: { id: number, name: string, format: string, coaches: number, scores: Score[] }[];
}

function ScoreRow({ score, rank, rubric }: { score: Score, rank: number, rubric: Category[] }) {
	const [open, setOpen] = useState(false);
	return <>
		<tr class="choosable" onClick={() => setOpen(!open)} title="Show the commentary">
			<td class="num">{rank}</td>
			<td><strong>{score.coach}</strong> <span class="muted small">pool {score.pool}</span></td>
			{rubric.map(category => <td key={category.id} class="num" title={score.notes[category.id] || ''}>{score.scores[category.id]}</td>)}
			<td class="num"><strong>{score.total}</strong></td>
			<td class="small">{score.band}</td>
		</tr>
		{open && <tr><td colSpan={rubric.length + 4}>
			<p style="margin-top:0">{score.comments}</p>
			<div class="grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr))">
				<div><strong class="small">Strengths</strong><ul class="small">{score.strengths.map(s => <li key={s}>{s}</li>)}</ul></div>
				<div><strong class="small">Weaknesses</strong><ul class="small">{score.weaknesses.map(s => <li key={s}>{s}</li>)}</ul></div>
				<div><strong class="small">Why each score</strong><ul class="small">
					{rubric.map(category => score.notes[category.id] && <li key={category.id}>{category.name}: {score.notes[category.id]}</li>)}
				</ul></div>
			</div>
			<p class="muted small" style="margin-bottom:0">
				{score.roster.map(mon => `${mon.pokemon}${mon.teraCaptain ? ' ★' : ''} (${mon.cost})`).join(' · ')}
				{' — '}scored {ago(score.scoredAt)} by {score.model}
			</p>
		</td></tr>}
	</>;
}

export function RosterScoresPage() {
	const me = useMe();
	const [view, setView] = useState<View | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [reloads, setReloads] = useState(0);

	useEffect(() => {
		if (!me?.user?.isAdmin) return;
		let timer: ReturnType<typeof setTimeout>;
		api.get<View>('/api/ai/roster-scores').then(loaded => {
			setView(loaded);
			setError(null);
			// follow a run while it goes
			if (loaded.status?.running) timer = setTimeout(() => setReloads(n => n + 1), 3000);
		}, setError);
		return () => clearTimeout(timer);
	}, [me?.user?.id, reloads]);

	if (!me) return <Loading />;
	if (!me.user?.isAdmin) return <p class="notice bad">Only the site's administrators can see roster scores.</p>;
	if (error) return <ErrorBox error={error} />;
	if (!view) return <Loading />;

	const start = async (leagueId?: number) => {
		try {
			const result = await api.post<{ leagues: string[] }>('/api/ai/roster-scores', leagueId ? { leagueId } : {});
			toast(`Scoring ${result.leagues.join(' and ')}…`, 'good');
			setReloads(n => n + 1);
		} catch (e) {
			toast((e as Error).message, 'bad');
		}
	};
	const { status, rubric } = view;
	return <>
		<div class="row" style="justify-content:space-between">
			<div>
				<p class="small" style="margin:0"><Link href="/admin">Admin</Link> /</p>
				<h1 style="margin:0">AI roster scores</h1>
			</div>
			<span class="row">
				<a class="button" href="/api/ai/roster-scores.csv">Download CSV</a>
				<ActionButton class="primary" disabled={!view.enabled || !!status?.running}
					confirm="Score every coach's roster in the VGC divisions? Each roster is one request to OgreClan AI's model."
					action={() => start()}>Score all VGC divisions</ActionButton>
			</span>
		</div>
		<p class="muted small">OgreClan AI scores each roster against the Power Rankings rubric, from the roster's Pokémon data (types, stats,
			Speed, abilities, the support moves each can learn, shared weaknesses and coverage, and points). These are editorial preseason scores,
			not results: use them as a starting point. Only the VGC divisions are scored. Hover a score for its reason; click a coach for the
			commentary.</p>
		{!view.enabled && <p class="notice">OgreClan AI isn't set up, so rosters can't be scored right now.</p>}
		{status && <p class={`notice ${status.running ? '' : status.failed.length || status.stopped ? 'warn' : 'good'} small`}>
			{status.running ? `Scoring… ${status.done} of ${status.total} rosters done.` :
				`Last run by ${status.by}, ${ago(status.finishedAt || status.startedAt)}: ${status.done - status.failed.length} of ${status.total} scored.`}
			{status.stopped && <> {status.stopped}</>}
			{status.failed.length > 0 && <> Not scored: {status.failed.map(f => `${f.coach} (${f.error})`).join(', ')}.</>}
		</p>}
		{view.leagues.map(league => <div key={league.id} class="card">
			<div class="row" style="justify-content:space-between">
				<h2 style="margin:0">{league.name}</h2>
				<ActionButton class="small" disabled={!view.enabled || !!status?.running} action={() => start(league.id)}>Score this division</ActionButton>
			</div>
			<p class="muted small" style="margin:2px 0 8px">{league.format} · {league.scores.length} of {league.coaches} coaches scored</p>
			{league.scores.length ? <div class="table-wrap"><table>
				<thead><tr><th class="num">#</th><th>Coach</th>
					{rubric.map(category => <th key={category.id} class="num" title={category.evaluate}>{category.name} <span class="muted">/{category.max}</span></th>)}
					<th class="num">Overall <span class="muted">/{view.outOf}</span></th><th>Band</th></tr></thead>
				<tbody>{league.scores.map((score, i) => <ScoreRow key={score.playerId} score={score} rank={i + 1} rubric={rubric} />)}</tbody>
			</table></div> : <p class="empty">Not scored yet.</p>}
		</div>)}
		{!view.leagues.length && <p class="empty">There are no VGC divisions to score.</p>}
	</>;
}
